import { type ComposerImageAttachment, useComposerDraftStore } from "../../composerDraftStore";
import { useComposerHandleContext } from "../../composerHandleContext";
import {
  type TerminalContextDraft,
  type TerminalContextSelection,
} from "../../lib/terminalContext";
import { type ChatComposerHandle } from "./ChatComposerHandle";
import { publishComposerTerminalContextHandle } from "./terminalContextCoordinator";
import { useCallback, useRef, useState } from "react";

export function useChatComposerBindings() {
  const draftActions = useChatComposerDraftActions();
  const refs = useChatComposerRefs();

  return {
    ...draftActions,
    ...refs,
  };
}

function useChatComposerDraftActions() {
  return {
    setComposerPendingAutoSubmit: useComposerDraftStore((store) => store.setPendingAutoSubmit),
    readComposerDraft: useComposerDraftStore((store) => store.getComposerDraft),
    stageComposerSubmission: useComposerDraftStore((store) => store.stageComposerSubmission),
    setStagedComposerExecutionIntent: useComposerDraftStore(
      (store) => store.setStagedComposerExecutionIntent,
    ),
    clearStagedComposerSubmission: useComposerDraftStore(
      (store) => store.clearStagedComposerSubmission,
    ),
    setComposerDraftPrompt: useComposerDraftStore((store) => store.setPrompt),
    addComposerDraftImages: useComposerDraftStore((store) => store.addImages),
    setComposerDraftFiles: useComposerDraftStore((store) => store.setFiles),
    setComposerDraftTerminalContexts: useComposerDraftStore((store) => store.setTerminalContexts),
    setComposerDraftModelSelection: useComposerDraftStore((store) => store.setModelSelection),
    setComposerDraftRuntimeMode: useComposerDraftStore((store) => store.setRuntimeMode),
    setComposerDraftInteractionMode: useComposerDraftStore((store) => store.setInteractionMode),
    clearComposerDraftContent: useComposerDraftStore((store) => store.clearComposerContent),
    setDraftThreadContext: useComposerDraftStore((store) => store.setDraftThreadContext),
  };
}

/**
 * The composer's refs and readiness, plus publication of the imperative
 * handle to the terminal-context bridge while this composer is mounted.
 */
function useChatComposerRefs() {
  const promptRef = useRef("");
  const composerImagesRef = useRef<ComposerImageAttachment[]>([]);
  const composerTerminalContextsRef = useRef<TerminalContextDraft[]>([]);
  const localComposerRef = useRef<ChatComposerHandle | null>(null);
  const composerRef = useComposerHandleContext() ?? localComposerRef;
  const composerReadyRef = useRef(false);
  const [composerReady, setComposerReady] = useState(false);

  const setComposerHandle = useCallback(
    (handle: ChatComposerHandle | null) => {
      composerRef.current = handle;
      // Terminal surfaces outside ChatView's subtree (the right panel's
      // Terminal tab) reach "Add to chat" through this bridge; see
      // terminalContextCoordinator.
      publishComposerTerminalContextHandle(
        handle === null
          ? null
          : { addTerminalContext: (selection) => handle.addTerminalContext(selection) },
      );
      if (handle && !composerReadyRef.current) {
        composerReadyRef.current = true;
        setComposerReady(true);
      }
    },
    [composerRef],
  );

  const focusComposer = useCallback(() => {
    composerRef.current?.focusAtEnd();
  }, [composerRef]);

  const restoreComposerAfterTimelineReachedEnd = useCallback(() => {
    composerRef.current?.restoreAfterTimelineReachedEnd();
  }, [composerRef]);

  const scheduleComposerFocus = useCallback(() => {
    window.requestAnimationFrame(() => {
      focusComposer();
    });
  }, [focusComposer]);

  const addTerminalContextToDraft = useCallback(
    (selection: TerminalContextSelection) => {
      composerRef.current?.addTerminalContext(selection);
    },
    [composerRef],
  );

  return {
    promptRef,
    composerImagesRef,
    composerTerminalContextsRef,
    composerRef,
    composerReady,
    setComposerHandle,
    focusComposer,
    restoreComposerAfterTimelineReachedEnd,
    scheduleComposerFocus,
    addTerminalContextToDraft,
  };
}
